import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminFunctions from './AdminFunctions';
import AuthFunctions from '../services/AuthService';
import Session from '../auth/Session';
import Select from 'react-select';

import {
  Container,
  Row,
  Col,
  Button,
  Spinner,
  Table,
  Input,
  Alert,
} from 'reactstrap';

const VehicleAssignments = () => {
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(undefined);

    const [crewSearch, setCrewSearch] = useState('');

    // data
    const [crews, setCrews] = useState([]);
    const [equipment, setEquipment] = useState([]);

    // crewId -> selected equipment options (react-select option objects)
    const [assignments, setAssignments] = useState({});

    const navigate = useNavigate();

    useEffect(() => {
        async function isAuth() {
            const isAuthenticated = await AuthFunctions.getAuthenticatedAsync();
            if (!isAuthenticated) navigate('/login');
        }
        async function fetchIsAdmin() {
            const isAdmin = await AdminFunctions.isAdminAsync();
            if (!isAdmin) {
                navigate('/accessdenied');
                return;
            }
        }

        isAuth();
        fetchIsAdmin();
    }, [navigate]);

    useEffect(() => {
        loadPage();
    }, []);

    const loadPage = async () => {
        try {
        setError('');
        setLoading(true);
        setSuccess(undefined);

        const token = Session.getToken();
        const response = await fetch(`api/CrewVehicleAssignments?activeOnly=true`, {
            headers: !token ? {} : { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || 'Failed to load vehicle assignments.');
        }

        const page = await response.json();

        // Normalize crews into the shape the UI expects
        const crewRows = (page.crews || []).map((c) => ({
            id: c.crewId,
            name: c.crewName,
            colorHex: c.colorHex ?? undefined,
        }));

        setCrews(crewRows);
        setEquipment(page.options || []);

        // Build assignments state from backend equipmentIds (active-only options)
        const initial = {};
        const optionMap = new Map();

        (page.options || [])
            .filter((x) => x?.isActive !== false)
            .filter(
            (x) =>
                (x.category || '').toLowerCase() === 'vehicle' ||
                (x.category || '').toLowerCase() === 'trailer'
            )
            .forEach((x) => {
            const cat =
                (x.category || '').toLowerCase() === 'trailer' ? 'Trailer' : 'Vehicle';
            optionMap.set(x.id, {
                value: x.id,
                label: x.equipmentName,
                meta: { category: cat },
            });
            });

        crewRows.forEach((crew) => {
            const dtoCrew = (page.crews || []).find((c) => c.crewId === crew.id);
            const ids = dtoCrew?.equipmentIds || [];
            initial[crew.id] = ids
            .map((id) => optionMap.get(id))
            .filter(Boolean);
        });

        setAssignments(initial);
        } catch (e) {
        setError(e?.message || 'Failed to load vehicle assignments.');
        } finally {
        setLoading(false);
        }
    };

    const isVehicleOrTrailer = (row) =>
        (row?.category || '').toLowerCase() === 'vehicle' ||
        (row?.category || '').toLowerCase() === 'trailer';

    // Base options: all active Vehicle/Trailer options, sorted
    const baseOptions = useMemo(() => {
        const filtered = (equipment || [])
        .filter((x) => x?.isActive !== false)
        .filter(isVehicleOrTrailer);

        const mapped = filtered.map((x) => {
        const cat =
            (x.category || '').toLowerCase() === 'trailer' ? 'Trailer' : 'Vehicle';
        return {
            value: x.id,
            label: x.equipmentName,
            meta: { category: cat },
        };
    });

    mapped.sort((a, b) => {
        if (a.meta.category !== b.meta.category) {
            return a.meta.category === 'Vehicle' ? -1 : 1;
        }
        return a.label.localeCompare(b.label);
    });

        return mapped;
    }, [equipment]);

    const filteredCrews = useMemo(() => {
        const term = crewSearch.trim().toLowerCase();
        if (!term) return crews;

        return crews.filter((c) => (c.name || '').toLowerCase().includes(term));
    }, [crews, crewSearch]);

    const handleChangeCrewAssignment = (crewId, selected) => {
        const next = selected || [];
        setAssignments((prev) => ({ ...prev, [crewId]: next }));
        setSuccess(undefined);
    };

    /**
     * Build an ownership map from current selections:
     * equipmentId -> crewId
     *
     * This lets us hide options that are already assigned to another crew.
     */
    const assignedToCrewByEquipmentId = useMemo(() => {
        const map = new Map();

        Object.keys(assignments).forEach((crewIdStr) => {
        const crewId = Number(crewIdStr);
        const selected = assignments[crewId] || [];
        selected.forEach((opt) => {
            if (opt?.value != null) {
            map.set(Number(opt.value), crewId);
            }
        });
        });

        return map;
    }, [assignments]);

    const handleSave = async () => {
        try {
            setSaving(true);
            setError('');
            setSuccess(undefined);

            const payload = {
                rows: Object.keys(assignments).map((crewIdStr) => {
                const crewId = Number(crewIdStr);
                const selected = assignments[crewId] || [];
                return { crewId, equipmentIds: selected.map((x) => x.value) };
                }),
            };

            const token = Session.getToken();
            const response = await fetch(`api/CrewVehicleAssignments`, {
                method: 'PUT',
                headers: !token
                ? { 'Content-Type': 'application/json' }
                : { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const text = await response.text();
                throw new Error(text || 'Error updating Crew vehicle assignment!');
            }

            setSuccess(true);
        } catch (e) {
            setSuccess(false);
            setError(e?.message || 'Error updating Crew vehicle assignment!');
        } finally {
            setSaving(false);
        }
    };

    // react-select styling to feel closer to bootstrap inputs + ensure menu is above table
    const selectStyles = useMemo(() => {
        return {
        control: (base) => ({
            ...base,
            minHeight: 38,
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
        }),
        };
    }, []);

    /**
     * NEW: Get the allowed options for a given crew:
     * - show anything unassigned
     * - plus anything assigned to THIS crew (so they can see/edit what they already have)
     */
    const getOptionsForCrew = (crewId) => {
        return baseOptions.filter((opt) => {
        const ownerCrewId = assignedToCrewByEquipmentId.get(Number(opt.value));
        return ownerCrewId == null || ownerCrewId === crewId;
        });
    };

    return (
        <div>
            {error ? (
                <Row>
                    <Col>
                        <div className="alert alert-danger mb-0">{error}</div>
                    </Col>
                </Row>
            ) : null}

            {loading ? (
                <div
                style={{
                    marginTop: '2rem',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                    <Spinner />
                </div>
            ) : (
                <Container className="container-main">
                    {/* Header */}
                    <Row className="align-items-center g-3">
                        <Col>
                            <h3>Crew Vehicle Assignments</h3>
                            <div className="text-muted" style={{ marginTop: 4 }}>
                                Assign Vehicles/Trailers to each crew. Vehicles are listed first, then
                                trailers. Vehicles already assigned to other crews are hidden.
                            </div>
                        </Col>

                        <Col xs="12" md="auto">
                            <div className="d-flex gap-3 w-100">
                                <Button
                                color="primary"
                                className="flex-fill flex-md-grow-0 text-nowrap"
                                onClick={handleSave}
                                disabled={saving}
                                >
                                {saving ? 'Saving...' : 'Save'}
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {/* Success / Fail */}
                    <Row style={{ marginTop: '20px' }}>
                        <Col>
                            {success === true && (
                                <Alert color="success">
                                {'Crew vehicle assignment updated successfully!'}
                                </Alert>
                            )}
                            {success === false && (
                                <Alert color="danger">
                                {'Error updating Crew vehicle assignment!'}
                                </Alert>
                            )}
                        </Col>
                    </Row>

                    {/* Search */}
                    <Row className="row-20" style={{ marginTop: 10 }}>
                        <Col md="6">
                            <Input
                                type="text"
                                value={crewSearch}
                                onChange={(e) => setCrewSearch(e.target.value)}
                                placeholder="Search crews..."
                            />
                        </Col>

                        <Col md="6" className="d-flex justify-content-md-end align-items-center">
                            <div className="text-muted" style={{ fontSize: 14 }}>
                                Showing {filteredCrews.length} of {crews.length}
                            </div>
                        </Col>
                    </Row>

                    {/* Table */}
                    <Row className="row-20" style={{ marginTop: 10 }}>
                        <Col>
                            <Table bordered hover responsive>
                                <thead>
                                    <tr>
                                        <th style={{ width: 280 }}>Crew</th>
                                        <th>Vehicles / Trailers</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {filteredCrews.map((crew) => {
                                        const selected = assignments[crew.id] || [];
                                        const rowOptions = getOptionsForCrew(crew.id);

                                        return (
                                        <tr key={crew.id}>
                                            <td style={{ verticalAlign: 'middle' }}>
                                                <div style={{ fontWeight: 600, color: crew.colorHex }}>
                                                    {crew.name}
                                                </div>
                                                <div className="text-muted" style={{ fontSize: 13 }}>
                                                    {selected.length} selected
                                                </div>
                                            </td>

                                            <td style={{ minWidth: 420 }}>
                                                <Select
                                                    isMulti
                                                    options={rowOptions}
                                                    value={selected}
                                                    onChange={(val) => handleChangeCrewAssignment(crew.id, val)}
                                                    placeholder="Select vehicles/trailers..."
                                                    closeMenuOnSelect={false}
                                                    styles={selectStyles}
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                />
                                            </td>
                                        </tr>
                                        );
                                    })}

                                    {filteredCrews.length === 0 && (
                                        <tr>
                                            <td colSpan={2} style={{ textAlign: 'center' }}>
                                                No crews found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Col>
                    </Row>
                </Container>
            )}
        </div>
    );
};

export default VehicleAssignments;